import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface User {
    id: number;
    name: string;
    email: string;
}

interface AssignModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAssign: (userId: number, comment?: string) => Promise<void>;
    users: User[];
    title?: string;
    isSubmitting?: boolean;
}

const AssignModal: React.FC<AssignModalProps> = ({
    isOpen,
    onClose,
    onAssign,
    users,
    title = 'Assign Task',
    isSubmitting = false
}) => {
    const { darkMode } = useTheme();
    const [selecteduserId, setSelectedUserId] = useState<number | ''>('');
    const [comment, setComment] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedUserId('');
            setComment('');
            setError('');
        }
    }, [isOpen]);

    const handleSubmit = async () => {
        if (!selecteduserId) {
            setError('Silakan pilih user');
            return;
        }

        try {
            await onAssign(Number(selecteduserId), comment);
        } catch (err: any) {
            setError(err.message || 'Gagal melakukan assign');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className={`w-full max-w-md rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                {/* Header */}
                <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    } rounded-t-lg`}>
                    <div className="flex items-center justify-between">
                        <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {title}
                        </h2>
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            className={`p-2 rounded-lg transition-colors ${darkMode
                                    ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                                    : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            <i className="fa-solid fa-times text-xl"></i>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    {error && (
                        <div className={`px-4 py-3 rounded-lg border ${darkMode
                                ? 'bg-red-900/30 border-red-800 text-red-300'
                                : 'bg-red-50 border-red-200 text-red-700'
                            }`}>
                            {error}
                        </div>
                    )}

                    <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                            Assign To <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selecteduserId}
                            onChange={(e) => setSelectedUserId(Number(e.target.value))}
                            disabled={isSubmitting}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 ${darkMode
                                    ? 'bg-gray-900 border-gray-700 text-white'
                                    : 'border-gray-300 text-gray-900'
                                }`}
                        >
                            <option value="">Pilih User</option>
                            {users.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.name} ({user.email})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                            Komentar (Optional)
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            disabled={isSubmitting}
                            placeholder="Tambahkan catatan..."
                            rows={3}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none ${darkMode
                                    ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                    : 'border-gray-300'
                                }`}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className={`px-6 py-4 border-t ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    } rounded-b-lg`}>
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            className={`px-4 py-2 border rounded-lg font-medium transition-colors ${darkMode
                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="px-4 py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 transition-colors flex items-center"
                        >
                            {isSubmitting && <i className="fa-solid fa-spinner fa-spin mr-2"></i>}
                            Assign & Update
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssignModal;
